import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EntityDetailActions } from 'actions';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import {
    setMarketingConsentAccount,
    setMarketingConsentContact,
} from 'services/MarketingConsentService';
import { canManageMarketingCampaigns } from 'containers/campaigns/marketing/utils/marketingCampaignPermissions';
import MarketingStatusCell from './MarketingStatusSelect';
import {
    createSetMarketingConsentWithRowPatch,
    getMarketingStatusIdFromRow,
    getMarketingStatusRowPatchFields,
} from './helpers';

function getConsentSetter(entity) {
    if (entity.entity === COMPANIES.entity) {
        return setMarketingConsentAccount;
    }
    if (entity.entity === CONTACTS.entity) {
        return setMarketingConsentContact;
    }
    throw new Error('MarketingStatusDetailField: unsupported entity');
}

const MarketingStatusDetailField = memo(({ entity, record, isReadOnly: isReadOnlyProp }) => {
    const dispatch = useDispatch();
    const marketingCampaignsEnabled = useSelector(
        (state) => state?.config?.userData?.marketingCampaigns,
    );
    const permission = useSelector((state) => state?.config?.permission);
    const canManage = useSelector(canManageMarketingCampaigns);
    const marketingFieldConfig = useSelector(
        (state) =>
            state.config?.standardFieldsSchemaMap?.[entity.extraFieldName]?.marketingStatus ?? null,
    );

    const visible =
        marketingCampaignsEnabled &&
        (marketingFieldConfig == null || !marketingFieldConfig.isHidden);

    const crudPermissions = permission?.crud_permission?.[entity.permission] ?? null;
    const canUpdate = !!crudPermissions?.update;

    const persistMarketingConsent = useMemo(() => {
        const base = createSetMarketingConsentWithRowPatch(getConsentSetter(entity), entity);

        return async (args) => {
            const result = await base(args);
            dispatch(
                EntityDetailActions.updateDetailFields(
                    entity,
                    args.id,
                    getMarketingStatusRowPatchFields(args.marketingStatusId),
                ),
            );
            return result;
        };
    }, [dispatch, entity]);

    const rowData = useMemo(() => {
        if (!record?.id) return null;
        const statusId = getMarketingStatusIdFromRow(record);
        return {
            ...record,
            Id: record.id,
            MarketingStatusId: statusId,
            marketingStatusId: statusId,
        };
    }, [record]);

    const value = getMarketingStatusIdFromRow(rowData);

    const isReadOnly =
        isReadOnlyProp ||
        !canUpdate ||
        !canManage ||
        getBackendBoolean(record?.isReadOnly) ||
        !!marketingFieldConfig?.readOnly;

    if (!visible || !rowData) {
        return null;
    }

    return (
        <MarketingStatusCell
            data={rowData}
            value={value}
            setMarketingConsent={persistMarketingConsent}
            entity={entity}
            isReadOnly={isReadOnly}
            showHistory
        />
    );
});

MarketingStatusDetailField.displayName = 'MarketingStatusDetailField';

export default MarketingStatusDetailField;
